import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Settings, ExternalLink } from 'lucide-react';
import { Switch } from '@/ui/switch';
import { Button } from '@/ui/Button';
import { Repository } from '@/shared/services/supabaseService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/table';

interface RepositoryListProps {
  repositories: Repository[];
  onToggleReview: (repoId: string, enabled: boolean) => void;
}

export const RepositoryList: React.FC<RepositoryListProps> = ({ repositories, onToggleReview }) => {
  if (repositories.length === 0) {
    return (
      <div className="p-12 text-center border-2 border-neutral-200 dark:border-neutral-800 border-dashed">
        <Github className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-mono text-lg font-bold uppercase">No Repositories Found</h3>
        <p className="text-muted-foreground font-mono text-sm mt-2">
            Connect your GitHub installation to see your repositories here.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-neutral-950 dark:border-neutral-100 bg-background">
      <Table>
        <TableHeader className="bg-neutral-100 dark:bg-neutral-900">
          <TableRow className="hover:bg-transparent border-b border-neutral-950 dark:border-neutral-100">
            <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10 w-[50%]">Repository</TableHead>
            <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10 text-center">Reviews</TableHead>
            <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {repositories.map((repo) => (
            <TableRow key={repo.id} className="border-b border-neutral-950 dark:border-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-900/20 transition-colors last:border-b-0">
              <TableCell className="py-4">
                <div className="flex items-start gap-3">
                   <div className="mt-1 p-1 bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                        <Github className="h-4 w-4" />
                   </div>
                   <div>
                        <Link to={`/repository/${repo.id}`} className="font-bold font-mono text-sm hover:underline hover:text-blue-600 dark:hover:text-blue-400 block mb-1">
                            {repo.name}
                        </Link>
                        <p className="text-xs text-muted-foreground line-clamp-1 max-w-md font-mono">
                            {repo.description || 'No description provided'}
                        </p>
                   </div>
                </div>
              </TableCell>
              <TableCell className="text-center py-4">
                 <div className="flex justify-center">
                    <Switch
                        checked={repo.review_enabled}
                        onCheckedChange={(checked) => onToggleReview(repo.id, checked)}
                        className="data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500"
                    />
                 </div>
              </TableCell>
              <TableCell className="text-right py-4">
                <div className="flex justify-end gap-2">
                    <Link to={`/repository/${repo.id}`}>
                        <Button variant="outline" size="sm" className="font-mono text-xs h-8 border-neutral-300 dark:border-neutral-700">
                            <Settings className="h-3 w-3 mr-2" />
                            CONFIGURE
                        </Button>
                    </Link>
                    <a href={repo.url} target="_blank" rel="noopener noreferrer">
                         <Button variant="ghost" size="icon" className="h-8 w-8">
                            <ExternalLink className="h-3 w-3" />
                         </Button>
                    </a>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
